import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getTopDeals } from "../api/dealflowApi";
import DealCard from "./DealCard";

/**
 * TopDealsSection — surfaces up to 6 strict-signal deals at the top of
 * the authenticated dashboard.
 *
 * Conservative gates enforced server-side (see
 * `app/services/feed/top_deals.py`):
 *   • only strict signals (strong_under_market | under_market)
 *   • confidence_score ≥ 0.30
 *   • market_signal_caveat IS NULL
 *   • image_health alive (with unchecked as fallback when alive cohort is thin)
 *   • country isolation
 */
function TopDealsTabs({ value, onChange }) {
  const { t } = useTranslation("topdeals");
  const tabs = [
    { key: "all", label: t("section.tabAll") },
    { key: "auto", label: t("section.tabAuto") },
    { key: "immo", label: t("section.tabImmo") },
  ];
  return (
    <div
      data-testid="top-deals-tabs"
      className="inline-flex rounded-xl border border-zinc-800 bg-zinc-950 p-1"
    >
      {tabs.map((t) => (
        <button
          key={t.key}
          data-testid={`top-deals-tab-${t.key}`}
          onClick={() => onChange(t.key)}
          className={`px-4 py-1.5 text-sm rounded-lg transition ${
            value === t.key
              ? "bg-emerald-500 text-zinc-950 font-medium"
              : "text-zinc-300 hover:text-zinc-100"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function TopDealsEmpty({ meta }) {
  const { t } = useTranslation("topdeals");
  // Phase 1.5 i18n fix — the backend message is German-only; prefer the
  // localized mapping of `empty_reason` and only fall back to the raw
  // server string when the code is unknown.
  const code = meta?.empty_reason;
  const localized = code
    ? t(`section.emptyReason.${code}`, { defaultValue: "" })
    : "";
  const message =
    localized || meta?.empty_reason_message || t("section.emptyDefault");
  return (
    <div
      data-testid="top-deals-empty"
      className="rounded-2xl border border-zinc-800 bg-zinc-950/40 p-6 text-sm text-zinc-400"
    >
      <div data-testid="top-deals-empty-message">{message}</div>
      {meta?.materializer_stale && (
        <div
          data-testid="top-deals-materializer-stale"
          className="mt-2 text-[11px] text-amber-400/80"
        >
          {t("section.updating")}
        </div>
      )}
    </div>
  );
}

function TopDealsLoading() {
  return (
    <div
      data-testid="top-deals-loading"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="aspect-[4/3] rounded-2xl border border-zinc-800 bg-zinc-900/50 animate-pulse"
        />
      ))}
    </div>
  );
}

export default function TopDealsSection({ hideHeader = false }) {
  const { t } = useTranslation("topdeals");
  const [vertical, setVertical] = useState("all");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["top-deals", vertical],
    queryFn: () => getTopDeals({ vertical, limit: 6 }),
    staleTime: 60_000,
  });

  // If the section errors out (e.g. the user just registered and the
  // request fails for some odd reason), fail-quiet: never block the
  // regular DealFlow grid below.
  if (isError) return null;

  const items = data?.items || [];
  const totalStrict = data?.meta?.strict_under_market_total;

  return (
    <section
      data-testid="top-deals-section"
      className="space-y-4 pb-2 border-b border-zinc-900"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        {hideHeader ? (
          <div />
        ) : (
          <div>
            <h2 className="text-xl text-zinc-100 font-medium tracking-tight">
              {t("section.title")}
            </h2>
            <p className="text-sm text-zinc-500 mt-1">
              {t("section.subtitle")}
            </p>
          </div>
        )}
        <TopDealsTabs value={vertical} onChange={setVertical} />
      </div>

      {isLoading ? (
        <TopDealsLoading />
      ) : items.length === 0 ? (
        <TopDealsEmpty meta={data?.meta} />
      ) : (
        <div
          data-testid="top-deals-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {data?.meta?.insight_bypass_active && (
            <div
              data-testid="top-deals-insight-pending-banner"
              className="col-span-full text-[11px] text-amber-400/70 -mt-1"
            >
              {t("section.insightPending")}
            </div>
          )}
          {items.map((c) => (
            <DealCard key={c.id} card={c} expanded />
          ))}
        </div>
      )}
    </section>
  );
}
